import type { BcfTopic } from './lib/bcfParser'
import { etiquetasPassam } from './lib/facetasDasEtiquetas'

export type DueFilter ='any' | 'overdue' | 'thisWeek' | 'thisMonth' | 'noDue'

export interface BcfFilters {
  search: string
  statuses: Set<string>
  priorities: Set<string>
  types: Set<string>
  assignees: Set<string> // free-form strings; "" means "Sem atribuição"
  labels: Set<string>
  due: DueFilter
  showClosed: boolean
  sort: 'index' | 'creation' | 'modification' | 'due'
}

export const EMPTY_FILTERS: BcfFilters = {
  search: '',
  statuses: new Set(),
  priorities: new Set(),
  types: new Set(),
  assignees: new Set(),
  labels: new Set(),
  due: 'any',
  showClosed: true,
  sort: 'index',
}

export function activeFilterCount(f: BcfFilters): number {
  let n = 0
  if (f.search.trim()) n++
  n +=
    f.statuses.size
    + f.priorities.size
    + f.types.size
    + f.assignees.size
    + f.labels.size
  if (f.due !== 'any') n++
  if (!f.showClosed) n++
  return n
}

export function isWithinDays(iso: string | null | undefined, days: number): boolean {
  if (!iso) return false
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return false
  const diffMs = d.getTime() - Date.now()
  return diffMs >= 0 && diffMs <= days * 24 * 60 * 60 * 1000
}

export function isOverdue(iso: string | null | undefined): boolean {
  if (!iso) return false
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return false
  return d.getTime() < Date.now()
}

/** Os campos do tópico que os filtros lêem. Os pins sobre o modelo
 *  (viewer/bcf/lib/pinFilter.ts) passam por aqui sem terem o tópico
 *  inteiro. */
export type CamposFiltraveis = Pick<
  BcfTopic,
  'status' | 'priority' | 'type' | 'assignedTo' | 'labels' | 'dueDate' | 'title' | 'description'
>

/** Um tópico passa os filtros da lista?
 *
 *  Por facetas: dentro de cada critério basta um dos valores escolhidos
 *  (OU); entre critérios, todos (E); um critério sem nada escolhido não
 *  filtra. As etiquetas dividem-se em facetas pelo tipo (especialidade,
 *  nível, grupo, outras; `facetasDasEtiquetas.ts`), para "MAC + HID +
 *  Nível P2" dar os de MAC ou HID no P2. Até à beta.176 as etiquetas eram
 *  todas em E, e duas especialidades davam zero. */
export function topicoPassaFiltros(t: CamposFiltraveis, filters: BcfFilters): boolean {
  if (filters.statuses.size > 0 && !filters.statuses.has(t.status ?? '')) return false
  if (filters.priorities.size > 0 && !filters.priorities.has(t.priority ?? '')) return false
  if (filters.types.size > 0 && !filters.types.has(t.type ?? '')) return false
  if (filters.assignees.size > 0 && !filters.assignees.has(t.assignedTo ?? '')) return false
  if (!etiquetasPassam(t.labels, filters.labels)) return false
  if (filters.due === 'overdue' && !isOverdue(t.dueDate)) return false
  if (filters.due === 'thisWeek' && !isWithinDays(t.dueDate, 7)) return false
  if (filters.due === 'thisMonth' && !isWithinDays(t.dueDate, 31)) return false
  if (filters.due === 'noDue' && t.dueDate) return false
  if (!filters.showClosed && /closed|fechado/i.test(t.status ?? '')) return false
  const q = filters.search.trim().toLowerCase()
  if (q) {
    const hay = `${t.title} ${t.description ?? ''}`.toLowerCase()
    if (!hay.includes(q)) return false
  }
  return true
}

/** A lista do módulo: os tópicos que passam os filtros, pela ordem
 *  escolhida. A lista e os pins que seguem o módulo usam esta função, para
 *  os dois nunca divergirem. */
export function filtrarTopicos(topics: readonly BcfTopic[], filters: BcfFilters): BcfTopic[] {
  return topics
    .filter((t) => topicoPassaFiltros(t, filters))
    .sort((a, b) => {
      if (filters.sort === 'index') {
        if (a.index !== null && b.index !== null) return a.index - b.index
        return (a.creationDate ?? '').localeCompare(b.creationDate ?? '')
      }
      if (filters.sort === 'creation') {
        return (b.creationDate ?? '').localeCompare(a.creationDate ?? '')
      }
      if (filters.sort === 'due') {
        // Os tópicos sem prazo vão para o fim.
        const ad = a.dueDate ?? '￿'
        const bd = b.dueDate ?? '￿'
        return ad.localeCompare(bd)
      }
      return (b.modifiedDate ?? '').localeCompare(a.modifiedDate ?? '')
    })
}
